// One renderer per widget type over the figures the API computed
// The page renders the figures `POST /dashboard/data` computed and derives none
// of its own. **That rule is the widget contract and it is not ZENITH.md §16**,
// which these comments cited until #1200: §16 is *AI assistance*, and what it
// rules is that the **model** is given computed numbers rather than raw
// controller payloads. The nearest written authority for the page is
// `conventions/dashboards-as-code.md` — a panel never renders an absent
// capability as zero — which ADR-0219 decision 2 is an instance of.
// Each body draws with the chart kit; the frame around it is the grid's.
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { EntityRef } from "../shell/EntityRef";
import type { Entity } from "../shell/entity";
import { ScopeLink, ScopePickLink, scopeSearch } from "../shell/scope";
import type { components } from "../api/schema";
import { Pill, Ticks, When } from "../ui";
import { BarList, Gauge, SegmentBar, Stat, type Tone } from "./charts";
import { count, plural, rssiBucket, seriesColor } from "./format";
import { SortHeader, nextSort, sortRows, type Sort, type SortValue } from "../pages/devices/narrow";
import { listed, wordFor, unrecognized } from "../api/openEnum";

export type WidgetResult = components["schemas"]["WidgetResult"];

/** Widgets whose body is one figure, centred in a short card. */
export const CENTERED: ReadonlySet<string> = new Set(["health", "clients"]);

export function WidgetBody({ result }: { result?: WidgetResult }) {
  if (!result) return <div className="skeleton" aria-label="Loading" />;
  if (result.error) return <p className="widget-failed"><strong>Could not load.</strong><span>{result.error}</span></p>;
  if (result.unavailable) return <p className="muted">{wordFor(UNAVAILABLE, result.unavailable.reason)}</p>;
  switch (result.type) {
    case "health": return result.health ? <Health f={result.health} /> : null;
    case "attention": return result.attention ? <Attention f={result.attention} /> : null;
    case "sites": return result.sites ? <Sites f={result.sites} /> : null;
    case "firmware": return result.firmware ? <Firmware f={result.firmware} /> : null;
    case "clients": return result.clients ? <Clients f={result.clients} /> : null;
    case "bands": return result.bands ? <Bands f={result.bands} /> : null;
    case "signal": return result.signal ? <Signal f={result.signal} /> : null;
    case "top_aps": return result.top_aps ? <TopAPs f={result.top_aps} /> : null;
    case "ssids": return result.ssids ? <SSIDs f={result.ssids} /> : null;
    case "organizations": return result.organizations ? <Organizations f={result.organizations} /> : null;
  }
  return null;
}

// Restates ADR-0243 decision 4 as ruled — signal and SSIDs are a live read of one site's access
// points, so they answer at one site's scope and are absent, with this reason, at any wider one
// until arpIQ Pulse computes them (ADR-0152 decision 3). Said in words and never drawn as an
// empty chart, which would read as a site with no clients (ADR-0219).
const UNAVAILABLE: Record<components["schemas"]["WidgetUnavailableReason"], string> = {
  scope_wider_than_one_site: "Shown for one site at a time. Choose a site to see it; across sites this figure is not available yet.",
};

type ReportingPair = { aps_reporting: number; aps_asked: number };

// Restates ADR-0222 decision 3 (ruled 3b; amended under #1345 for clients, bands and busiest
// access points) and ADR-0243 decision 4 — every client figure says how many of the online access
// points it covers, from the pair the server sent, so no layout shows a short figure as complete
// (#1396). For the three the listing feeds, "reporting" is an access point whose listing row
// carries its counts; for signal and SSIDs it is one whose state report describes it now, so a
// stale report is not reporting. The page derives neither number.
function Reporting({ f }: { f: ReportingPair }) {
  if (f.aps_asked === 0) return null;
  return <p className="widget-foot">{f.aps_reporting} of {f.aps_asked} access {plural(f.aps_asked, "point")} reporting</p>;
}

function healthTone(percent: number): Tone {
  return percent === 100 ? "good" : percent >= 90 ? "warn" : "bad";
}
const TONE_COLOR: Record<Tone, string> = { good: "var(--status-good)", warn: "var(--status-warning)", serious: "var(--status-serious)", bad: "var(--status-critical)" };

// Restates ADR-0219 decisions 2 and 3 — `percent` is the online share of the devices whose
// liveness is KNOWN, and its absence means nothing is known about any of them. **The absence
// is rendered as an absence and never computed around.** This body used to read
// `f.percent ?? (f.online / f.total) * 100`, which put the figure back the moment the server
// declined to state it, and `0%` is a figure an operator believes — it is the same number a
// site with every device genuinely down shows. That fallback was also a number the page
// derived, which the widget contract above forbids, and #1200 is what it cost.
function Health({ f }: { f: NonNullable<WidgetResult["health"]> }) {
  if (f.total === 0) {
    return <p className="muted">No devices{f.unreadable_sites > 0 && <>; {f.unreadable_sites} {plural(f.unreadable_sites, "site")} could not be read</>}.</p>;
  }
  // Restates ADR-0219 decision 3, ADR-0234 decisions 1 (`1c`) and 6 (`6a`) — a device with no
  // record is not known, which is not offline, and the record is the copy of the controller's
  // listing kept here, so the words say the listing. Corrected on #1678 (note `4005465441`): it
  // read "… Not the same as offline, and not waited out.", which is not so of a device that
  // connected after the copy was taken and is counted here until the next copy.
  const unknown = f.unknown > 0 && (
    <span className="small muted" title="The controller's listing holds no record for these devices, so nothing is known about whether they are up. That is not the same as offline.">
      {f.unknown} not known
    </span>
  );
  if (f.percent === undefined) {
    // The server states no percentage only when **every** device in scope is
    // unknown — `percent` is the online share of `total - unknown`, and the
    // zero-device case returned above — so this says that and does not guess
    // at a partial one.
    return (
      <div>
        <p className="muted">
          Nothing is known about {f.total === 1 ? "this device" : `these ${f.total} devices`}: the controller&apos;s listing
          holds no record {f.total === 1 ? "for it" : "for any of them"}, so it has not said whether
          {f.total === 1 ? " it is" : " they are"} up. Not the same as offline.
        </p>
        <ScopeLink to="/operate/devices">See devices →</ScopeLink>
      </div>
    );
  }
  const percent = f.percent;
  return (
    <div>
      <div className="health">
        <Gauge percent={percent} label="online" tone={healthTone(percent)} />
        <div className="health-facts">
          {f.breakdown.filter((b) => b.total > 0).map((b) => (
            <div key={b.label}>
              <b>{b.online}/{b.total}</b> {b.label.toLowerCase()}
              {b.unknown > 0 && <span className="small muted"> · {b.unknown} not known</span>}
            </div>
          ))}
          {f.offline > 0 && <ScopeLink to="/operate/devices">{f.offline} offline →</ScopeLink>}
          {unknown}
        </div>
      </div>
      {f.unreadable_sites > 0 && <p className="widget-foot">{f.unreadable_sites} {plural(f.unreadable_sites, "site")} could not be read, so the totals leave out {f.unreadable_sites === 1 ? "its" : "their"} devices.</p>}
    </div>
  );
}

function Clients({ f }: { f: NonNullable<WidgetResult["clients"]> }) {
  if (f.aps_asked === 0) return <p className="muted">No online access points in this scope.</p>;
  return (
    <Stat value={count(f.count)} label={`${plural(f.count, "client")} online`}
      sub={<>
        {f.unique_stations !== undefined && f.unique_stations !== f.count && <>{f.unique_stations} distinct clients · </>}
        {f.per_ap !== undefined && <>{f.per_ap} per access point · </>}
        {f.aps_reporting} of {f.aps_asked} access {plural(f.aps_asked, "point")} reporting
      </>} />
  );
}

/** Slowest band first: the slot stays tied to the band, not to its rank. */
const BAND_ORDER = ["2.4GHz", "5GHz", "6GHz"];

function Bands({ f }: { f: NonNullable<WidgetResult["bands"]> }) {
  if (f.total === 0) return <div><p className="muted">No clients on any band.</p><Reporting f={f} /></div>;
  const segments = f.series.slice()
    .sort((a, b) => slot(a.label) - slot(b.label))
    .map((s) => ({ key: s.label, label: s.label.replace("GHz", " GHz"), value: s.value, color: seriesColor(slot(s.label)) }));
  return <div><SegmentBar segments={segments} /><Reporting f={f} /></div>;
}
function slot(band: string): number {
  const i = BAND_ORDER.indexOf(band);
  return i === -1 ? 3 : i;
}

function Signal({ f }: { f: NonNullable<WidgetResult["signal"]> }) {
  if (f.total === 0) return <div><p className="muted">No clients to measure.</p><Reporting f={f} /></div>;
  const segments = f.series.map((b) => ({ key: b.key, label: b.label, value: b.value, color: `var(--rf-${b.key})` }));
  return (
    <div>
      <SegmentBar segments={segments} />
      <p className="widget-foot">
        {f.median_rssi !== undefined && <>Median {f.median_rssi} dBm · </>}
        good is {f.thresholds.good} dBm or better, fair down to {f.thresholds.fair} dBm
        {f.unknown > 0 && <> · {f.unknown} without a reading</>}
      </p>
      <Reporting f={f} />
    </div>
  );
}

function TopAPs({ f }: { f: NonNullable<WidgetResult["top_aps"]> }) {
  return (
    <div>
    <BarList
      rows={f.rows.map((r) => ({
        key: r.serial, text: r.name,
        label: <span title={r.site_name ? `${r.serial} · ${r.site_name}` : r.serial}>{r.name}</span>,
        value: r.clients,
        hint: [r.site_name, r.median_rssi !== undefined ? `median ${r.median_rssi} dBm` : undefined].filter(Boolean).join(" · ") || undefined,
        aside: r.median_rssi !== undefined ? <span className={"small signal-" + rssiBucket(r.median_rssi)}>{r.median_rssi} dBm</span> : undefined,
      }))}
      format={(v) => `${v} ${plural(v, "client")}`}
      empty={f.total_aps > 0 ? `No access point has clients (${f.total_aps} in scope).` : "No access point has clients."} />
    <Reporting f={f} />
    </div>
  );
}

function SSIDs({ f }: { f: NonNullable<WidgetResult["ssids"]> }) {
  return (
    <div>
      <BarList rows={f.series.map((s) => ({ key: s.label, label: s.label, value: s.value }))} colorBy="series" empty="No clients on any SSID." />
      <Reporting f={f} />
    </div>
  );
}

function Firmware({ f }: { f: NonNullable<WidgetResult["firmware"]> }) {
  if (f.total === 0) return <p className="muted">No online devices to count.</p>;
  return (
    <div>
      <BarList rows={f.series.map((s) => ({ key: s.label, text: s.label, label: <code>{s.label}</code>, value: s.value }))} format={(v) => `${v} ${plural(v, "device")}`} />
      <p className="widget-foot">{f.distinct} {plural(f.distinct, "version")} across {f.total} online {plural(f.total, "device")}.</p>
    </div>
  );
}

type AttentionFigures = NonNullable<WidgetResult["attention"]>;
type AttentionItem = AttentionFigures["items"][number];
// The named list: since ADR-0511 the field is the list or any other string.
type Kind = components["schemas"]["AttentionItemKind"];
type Counts = AttentionFigures["counts"];

// `count` and `headline` come from `counts`, which is complete, never from the
// items, which the server may have bounded (the widget contract: the page renders numbers it
// was given). `text` is the single-item wording and stays as it was.
const KIND: Record<Kind, {
  text: string; tone: "warn" | "critical"; verb: string; to: string; go: string;
  count: (c: Counts) => number; headline: (n: number) => string;
  /** Each member links to its own site's page rather than the kind to one list. */
  perSite?: boolean;
}> = {
  cell_failed: { text: "Controller did not answer", tone: "critical", verb: "Action", to: "/sites", go: "See sites",
    count: (c) => c.cells_failed, headline: (n) => `${n} ${plural(n, "controller")} did not answer` },
  offline_device: { text: "Offline", tone: "critical", verb: "Action", to: "/operate/devices", go: "See devices",
    count: (c) => c.offline_devices, headline: (n) => `${n} ${plural(n, "device")} offline` },
  // Restates ADR-0219 decision 3, ADR-0234 decisions 1 (`1c`) and 6 (`6a`) — the cell holding
  // no record for a device is not a kind of offline, so it is its own row with its own words.
  // Rendered as `offline_device` with no `since` it read as "gone quiet, last contact unknown"
  // and sent an operator to the site; the condition is at the cell. `warn` rather than
  // `critical` for the reason the convergence read's `unknown` is: it is an absence of
  // evidence, not a device known to be down. The record is Zenith's copy of the cell's
  // listing, as old as the age the footer shows for the page (#1315), so the words say the
  // listing and not the cell: a device that arrived after the copy was taken is counted here
  // until the next tick.
  unknown_device: { text: "No record in the controller's listing", tone: "warn", verb: "Check", to: "/operate/devices", go: "See devices",
    count: (c) => c.unknown_devices, headline: (n) => `The controller's listing holds no record for ${n} ${plural(n, "device")}` },
  // Restates ADR-0031 decision 9, ADR-0032 decisions 2 and 8 — the placement service chooses a
  // site's controller, and Sites, where this link goes, registers nothing: a manual row is
  // registered on the Cells page or at a terminal. Corrected on #1678 (note `4005465441`): the
  // link read "Register a controller".
  site_no_cell: { text: "No controller", tone: "warn", verb: "Check", to: "/sites", go: "See sites",
    count: (c) => c.sites_no_cell, headline: (n) => `${n} ${plural(n, "site")} without a controller` },
  // Restates ADR-0493 decisions 1 (`1f`) and 6a, built by #2105 — a site counted here has a
  // controller and no venue, and the first claim, import or plan there by a person holding
  // site.write creates it, so the operator is not sent to Sites first. The button stays, so the
  // link does too. A site with no controller is `site_no_cell`, above, and gets no venue from any act.
  site_no_venue: { text: "No venue yet: the first claim, import or plan creates it", tone: "warn", verb: "Check", to: "/sites", go: "Create the venue",
    count: (c) => c.sites_no_venue, headline: (n) => `${n} ${plural(n, "site")} without a venue yet: the first claim, import or plan at each creates it` },
  // Restates ADR-0222 decisions 2 and 3 (ruled 2b and 3b) and #1373 — an online access point whose
  // state report gave this page no stations is short on the figures, not off the air, so it is a
  // `Check` and not an `Action`. Why is in each member's `detail`, in Zenith's words: the cell
  // holds no report from it (the cell answered), the cell did not answer, or the report could not
  // be read. A stale report is its own row, because "not read" would be false of it (decision 2
  // rejected 2c). Each member opens its own site's Clients page, which names the same access points.
  state_not_read: { text: "Client figures not read", tone: "warn", verb: "Check", to: "/operate/wireless", go: "", perSite: true,
    count: (c) => c.aps_state_not_read, headline: (n) => `${n} ${plural(n, "access point")} whose client figures were not read` },
  stale_report: { text: "Reporting from a configuration it has left", tone: "warn", verb: "Check", to: "/operate/wireless", go: "", perSite: true,
    count: (c) => c.aps_stale_report, headline: (n) => `${n} ${plural(n, "access point")} reporting from a configuration ${n === 1 ? "it has" : "they have"} left` },
};

// The server's order, which `items` already reproduces: cells that did not
// answer, then sites that cannot be read, then offline devices. It is restated
// here for one case the list cannot express — a kind whose every member was cut
// by the bound has no entry to take a position from, and its count still has to
// be reported somewhere.
const KIND_ORDER: readonly Kind[] = ["cell_failed", "site_no_cell", "site_no_venue", "unknown_device", "offline_device", "state_not_read", "stale_report"];

/** One row per kind that has anything in it, in the order the server sent. */
function byKind(f: AttentionFigures): { kind: Kind; items: AttentionItem[]; n: number }[] {
  const members = new Map<Kind, AttentionItem[]>();
  for (const it of f.items) {
    if (!listed(KIND, it.kind)) continue;
    const seen = members.get(it.kind);
    if (seen) seen.push(it);
    else members.set(it.kind, [it]);
  }
  const kinds = [...members.keys(), ...KIND_ORDER.filter((k) => !members.has(k))];
  return kinds
    .map((kind) => ({ kind, items: members.get(kind) ?? [], n: KIND[kind].count(f.counts) }))
    .filter((g) => g.n > 0);
}

/** What one member is called, in the order the fields are worth showing. */
function named(it: AttentionItem): string | undefined {
  return it.name || it.serial || it.cell_id || it.site_name;
}

/** The device a member is, where it names one, and the site it is at, where it
 * names that: each opens the one menu (#2367). The item carries the levels above
 * its site, so the menu's scope is known without the estate. */
function deviceOf(it: AttentionItem): Entity | undefined {
  return it.serial ? { kind: "device", serial: it.serial, name: it.name || undefined, siteId: it.site_id, siteName: it.site_name, orgId: it.org_id, customerId: it.customer_id } : undefined;
}
function siteOf(it: AttentionItem): Entity | undefined {
  return it.site_id && it.site_name ? { kind: "site", id: it.site_id, name: it.site_name, orgId: it.org_id, customerId: it.customer_id } : undefined;
}

/** A member's name with its menu: the device's, else the site's where the name
 * is the site's, else the name alone (a controller has no menu). */
function MemberName({ it, children }: { it: AttentionItem; children: ReactNode }) {
  const device = deviceOf(it);
  const site = !device && !it.cell_id ? siteOf(it) : undefined;
  const entity = device ?? site;
  return entity ? <EntityRef entity={entity}>{children}</EntityRef> : <>{children}</>;
}

/** The member's site, named after it, with its menu. */
function MemberSite({ it }: { it: AttentionItem }) {
  const site = siteOf(it);
  return site ? <EntityRef entity={site}><span>{it.site_name}</span></EntityRef> : <>{it.site_name}</>;
}

// One offline device is worth naming; a dozen are worth counting. A kind with
// more than one member is one row — the count, the members' names, one link —
// so a scope with a long tail of offline devices does not push everything else
// off a 6x2 card. A kind with exactly one keeps the line it had, because that
// is where `detail` and `since` still say something specific.
function Attention({ f }: { f: NonNullable<WidgetResult["attention"]> }) {
  const groups = byKind(f);
  // Restates ADR-0511 decision 5a — an item of a kind this build does not know is listed as
  // received and marked unrecognized, and never dropped: a card reading "Nothing needs attention"
  // over an item it cannot name would say something false.
  const unlisted = f.items.filter((it) => !listed(KIND, it.kind));
  if (groups.length === 0 && unlisted.length === 0) return <p className="all-clear">Nothing needs attention.</p>;
  return (
    <div>
      <ul className="attention">
        {groups.map((g) => {
          const k = KIND[g.kind];
          const one = g.n === 1 && g.items.length === 1 ? g.items[0] : undefined;
          return (
            <li key={g.kind}>
              <Pill tone={k.tone}>{k.verb}</Pill>
              <div style={{ minWidth: 0 }}>
                {k.perSite ? <PerSite k={k} n={g.n} items={g.items} />
                  : <>
                      {one ? <One k={k} it={one} /> : <Bundle k={k} n={g.n} items={g.items} />}
                      <ScopeLink className="attention-go" to={k.to}>{k.go} →</ScopeLink>
                    </>}
              </div>
            </li>
          );
        })}
        {unlisted.map((it, i) => (
          <li key={`unlisted-${i}`}>
            <Pill tone="warn" title="This page does not recognize this kind of item. Reload the page: a newer version may know it.">{unrecognized(it.kind)}</Pill>
            <div style={{ minWidth: 0 }}><div className="attention-what">{named(it)}</div></div>
          </li>
        ))}
      </ul>
      {f.truncated && <p className="widget-foot">This list does not show every item. The counts are complete.</p>}
    </div>
  );
}

// `upstream` is the controller's own error text — its service names, its status
// codes, its vocabulary. Presence is the whole gate: the server sets the field
// only for a caller holding the superuser flag (§6.2) and omits it from
// everyone else's payload, so there is nothing for a customer to render and
// this component adds no role check that could disagree with the server. One
// rule, one implementation (§4.2). A bundled row still carries none — #61
// decided several controllers' words have nowhere sensible to go on one line.
function One({ k, it }: { k: (typeof KIND)[Kind]; it: AttentionItem }) {
  return (
    <>
      <div className="attention-what"><MemberName it={it}><span>{named(it)}</span></MemberName></div>
      <div className="attention-detail">
        {k.text}
        {it.site_name && it.kind !== "site_no_cell" && it.kind !== "site_no_venue" && <> · <MemberSite it={it} /></>}
        {it.since && <> · since <When value={it.since} /></>}
        {it.detail && it.kind !== "offline_device" && it.kind !== "unknown_device" && <> · <Ticks text={it.detail} /></>}
      </div>
      {it.upstream && <div className="attention-upstream" title={it.upstream}>{it.upstream}</div>}
    </>
  );
}

// The names clamp in CSS rather than to a count here, so a widget dragged wider
// shows more of them without the browser deciding how many fit. `n` is the
// estate's figure; `items` is only what the server chose to list, so the two
// differ under `truncated` and the headline is the one that must be complete.
function Bundle({ k, n, items }: { k: (typeof KIND)[Kind]; n: number; items: AttentionItem[] }) {
  const names = items.map(named).filter(Boolean).join(", ");
  return (
    <>
      <div className="attention-what">{k.headline(n)}</div>
      {names && <div className="attention-names" title={names}>{names}</div>}
    </>
  );
}

// The headline from `counts`, and each listed member as a link to its own site's page at that
// site's scope — the address names all three ids the item carries, so it works from all sites —
// with its site and why. A member the server sent without its site's ids is named and not linked.
function PerSite({ k, n, items }: { k: (typeof KIND)[Kind]; n: number; items: AttentionItem[] }) {
  return (
    <>
      <div className="attention-what">{k.headline(n)}</div>
      <div className="attention-members">
        {items.map((it) => {
          const name = named(it);
          const to = it.org_id && it.customer_id && it.site_id
            ? { pathname: k.to, search: scopeSearch({ orgId: it.org_id, customerId: it.customer_id, siteId: it.site_id }) }
            : undefined;
          return (
            <div key={`${it.site_id}/${it.serial}`}>
              <MemberName it={it}>{to ? <Link to={to} title={`${it.serial ?? ""} · ${it.site_name ?? ""}`}>{name}</Link> : <span>{name}</span>}</MemberName>
              {it.site_name && <> · <MemberSite it={it} /></>}
              {it.since && <> · <When value={it.since} /></>}
              {it.detail && <span className="muted"> · <Ticks text={it.detail} /></span>}
            </div>
          );
        })}
      </div>
    </>
  );
}

/** A word for every status the document declares (#193). `ok` is drawn as the
 * health bar rather than said, but a status without a word fails the
 * typecheck rather than rendering its wire word. */
const SITE_STATUS: Record<components["schemas"]["SiteHealthRowStatus"], string> = { ok: "Healthy", no_cell: "No controller", no_venue: "No venue", failed: "Controller did not answer" };

function Sites({ f }: { f: NonNullable<WidgetResult["sites"]> }) {
  if (f.rows.length === 0) return <p className="muted">No sites in this scope.</p>;
  return (
    <div>
      <table className="widget-table" aria-label="Sites by health">
        <thead><tr><th>Site</th><th className="num">Devices</th><th>Health</th></tr></thead>
        <tbody>
          {f.rows.map((r) => {
            // The denominator is the devices whose liveness is known (ADR-0219
            // decision 2), so a row whose cell said nothing shows no bar rather
            // than a 0% an operator would believe. `0/21` with 21 not known is
            // what #1200 drew as `Health 0%`.
            const known = r.total - r.unknown;
            const pct = r.status === "ok" && known > 0 ? Math.round((r.online / known) * 100) : undefined;
            return (
              <tr key={r.site_id}>
                {/* A site's name narrows the page to it, as picking it in the
                    scope switcher does (#1276); the list is the foot link. The
                    row names all three ids, so this works at all sites too. */}
                <td><EntityRef entity={{ kind: "site", id: r.site_id, name: r.name, orgId: r.org_id, customerId: r.customer_id }}><ScopePickLink scope={{ orgId: r.org_id, customerId: r.customer_id, siteId: r.site_id }} title={`Narrow to ${r.name}`}>{r.name}</ScopePickLink></EntityRef></td>
                <td className="num">{r.status === "ok" && r.total > 0 ? <>{r.online}/{r.total}</> : <span className="dim">—</span>}</td>
                <td>
                  {r.status !== "ok" ? <Pill tone="warn" title={r.detail}>{wordFor(SITE_STATUS, r.status)}</Pill>
                    : r.total === 0 ? <span className="dim">no devices</span>
                    : pct === undefined
                    ? <Pill tone="warn" title={`The controller's listing holds no record for ${r.unknown === r.total ? "any of this site's devices" : `${r.unknown} of ${r.total} of this site's devices`}, so nothing is known about whether they are up. Not the same as offline.`}>Not known</Pill>
                    : <div className="health-bar" title={`${r.online} of ${known} online${r.unknown > 0 ? `; the controller's listing holds no record for ${r.unknown} more` : ""}`}>
                        <div className="health-bar-track"><div className="health-bar-fill" style={{ width: `${pct}%`, background: TONE_COLOR[healthTone(pct)] }} /></div>
                        <span className="small muted tabular">{pct}%</span>
                        {r.unknown > 0 && <span className="small muted"> · {r.unknown} not known</span>}
                      </div>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="widget-foot"><ScopeLink to="/sites">See sites →</ScopeLink></p>
    </div>
  );
}

// Restates ADR-0219 decisions 2 and 3 — an organization's `percent` is the online share of its
// devices whose liveness is KNOWN, absent when none is, and the absence is said in words and
// never drawn as 0%. `unknown` is its own column and is not offline. Every figure in a row is
// the server's (#1589, #1720): the page computes none, and the two device classes are the
// server's own split of the row's counts.
//
// The rows open in the order the server sent, worst first. A click on a column head orders
// them by that column, a second turns it around and a third returns to the server's order
// (#1720). The order is this render's alone: it is not in the address, not remembered and not
// in the saved layout. An absent figure, a health nothing is known for or clients nobody was
// asked for, sorts last in both directions and never as zero.
type OrgRowT = NonNullable<WidgetResult["organizations"]>["rows"][number];
type OrgSortKey = "name" | "sites" | "aps" | "switches" | "unknown" | "health" | "clients" | "attention";
const ORG_SORT: Record<OrgSortKey, (r: OrgRowT) => SortValue> = {
  name: (r) => r.name || undefined,
  sites: (r) => r.sites,
  aps: (r) => r.access_points.total,
  switches: (r) => r.switches.total,
  unknown: (r) => r.unknown,
  health: (r) => r.percent,
  clients: (r) => (r.aps_reporting === 0 ? undefined : r.clients),
  attention: (r) => r.attention,
};

/** One device class of an organization's row, as online over total — the Sites widget's form.
 * An organization with none of the class reads a dim dash. A site that could not be read adds
 * its devices to no count (`sites_not_ok`), so the cell then says how many of the sites it
 * covers, as the Clients cell says how many access points reported, and a dash there never
 * reads as none. */
function ClassCount({ c, sites, notRead, noun }: { c: OrgRowT["access_points"]; sites: number; notRead: number; noun: string }) {
  const figure = c.total === 0 ? <span className="dim">—</span> : <>{c.online}/{c.total}</>;
  if (notRead > 0) {
    return (
      <span title={`${notRead} of this organization's ${plural(sites, "site")} could not be read, so this count is short by any ${noun} there.`}>
        {figure}<span className="small muted"> · {sites - notRead} of {sites}</span>
      </span>
    );
  }
  return figure;
}

function Organizations({ f }: { f: NonNullable<WidgetResult["organizations"]> }) {
  const [sort, setSort] = useState<Sort<OrgSortKey> | undefined>(undefined);
  const rows = useMemo(() => sortRows(f.rows, sort, ORG_SORT), [f.rows, sort]);
  if (f.rows.length === 0) return <p className="muted">No organizations in this scope.</p>;
  const head = (k: OrgSortKey, label: string, num = true) => <SortHeader k={k} sort={sort} onSort={(key) => setSort(nextSort(sort, key))} className={num ? "num" : undefined}>{label}</SortHeader>;
  return (
    <table className="widget-table" aria-label="Organizations by status">
      <thead>
        <tr>
          {head("name", "Organization", false)}{head("sites", "Sites")}{head("aps", "Access points")}{head("switches", "Switches")}
          {head("unknown", "Not known")}{head("health", "Health", false)}
          {head("clients", "Clients")}{head("attention", "Attention")}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.org_id}>
            {/* An organization's name narrows the page to it, as a site's does in the Sites
                widget (#1276); there the Sites widget lists that organization's sites. */}
            <td><ScopePickLink scope={{ orgId: r.org_id }} title={`Narrow to ${r.name || "this organization"}`}>{r.name || <span className="muted">Unnamed</span>}</ScopePickLink></td>
            <td className="num">
              {r.sites}
              {r.sites_not_ok > 0 && <span className="small muted" title="Sites whose devices could not be read: no controller, no venue, or a controller that did not answer. Their devices are in none of this row's device counts."> · {r.sites_not_ok} not read</span>}
            </td>
            {/* The server's class is every device that is not a switch, so a device of another
                kind is counted with the access points. */}
            <td className="num"><ClassCount c={r.access_points} sites={r.sites} notRead={r.sites_not_ok} noun="access points" /></td>
            <td className="num"><ClassCount c={r.switches} sites={r.sites} notRead={r.sites_not_ok} noun="switches" /></td>
            <td className="num">{r.unknown > 0 ? r.unknown : <span className="dim">0</span>}</td>
            <td>
              {r.total === 0 ? <span className="dim">no devices</span>
                : r.percent === undefined
                ? <Pill tone="warn" title="The controller's listing holds no record for any of this organization's devices, so nothing is known about whether they are up. Not the same as offline.">Not known</Pill>
                : <div className="health-bar" title={`${r.online} of ${r.total - r.unknown} online${r.unknown > 0 ? `; nothing is known about ${r.unknown} more` : ""}`}>
                    <div className="health-bar-track"><div className="health-bar-fill" style={{ width: `${r.percent}%`, background: TONE_COLOR[healthTone(r.percent)] }} /></div>
                    <span className="small muted tabular">{r.percent}%</span>
                  </div>}
            </td>
            <td className="num">
              {r.aps_asked === 0 ? <span className="dim">—</span>
                // No online access point reported, so there is no client figure, not a zero.
                : r.aps_reporting === 0 ? <span className="dim" title={`None of ${r.aps_asked} online access ${plural(r.aps_asked, "point")} reported its clients`}>—</span>
                : <span title={`${r.aps_reporting} of ${r.aps_asked} online access ${plural(r.aps_asked, "point")} reporting`}>
                    {count(r.clients)}{r.aps_reporting < r.aps_asked && <span className="small muted"> · {r.aps_reporting} of {r.aps_asked}</span>}
                  </span>}
            </td>
            <td className="num">{r.attention > 0 ? <Pill tone="warn">{r.attention}</Pill> : <span className="dim">0</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
